import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, FileText, ArrowLeft, ArrowRight, Check, X,
  AlertTriangle, Download, Loader2, Users, GitBranch, RotateCcw,
} from "lucide-react";
import { api, API, apiError } from "@/lib/api";
import { Btn, Input, Select, Textarea, Field, Panel } from "@/components/common";

const ENTITY_COLS = ["entity_id", "entity_type", "name", "first_name", "last_name", "title",
  "category", "group", "market", "phone", "email", "website", "state", "source", "notes", "date_identified"];
const REL_COLS = ["relationship_id", "from_entity_id", "to_entity_id", "connection_type",
  "state", "source", "notes", "date_documented"];

function csvSafe(v) {
  const s = v == null ? "" : String(v);
  if (/^[=+\-@]/.test(s)) return `'${s}`;
  return s;
}
function downloadCsv(filename, rows) {
  const body = rows.map((r) => r.map((c) => {
    const s = csvSafe(c);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",")).join("\n");
  const blob = new Blob([body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const STATUS_COLOR = {
  new: "var(--si-teal-light)", link: "var(--si-silver)", already_imported: "var(--si-silver)",
  invalid: "var(--si-danger)", skipped: "var(--si-text-2)", resolvable: "var(--si-teal-light)",
  unresolved: "var(--si-danger)",
};

export default function ImportWizard({ workspaceId, folders = [], existingGroups = [], onClose, onComplete }) {
  const [step, setStep] = useState("upload"); // upload | configure | review | result
  const [mode, setMode] = useState("files"); // files | zip
  const [entityFile, setEntityFile] = useState(null);
  const [relFile, setRelFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [reprChoice, setReprChoice] = useState(null);
  const [preferFormat, setPreferFormat] = useState("csv");

  // configure
  const [groupName, setGroupName] = useState("");
  const [groupDesc, setGroupDesc] = useState("");
  const [folderId, setFolderId] = useState("");
  const [namespace, setNamespace] = useState("");
  const [intoGroupId, setIntoGroupId] = useState("");
  const [entityMapping, setEntityMapping] = useState({});
  const [relMapping, setRelMapping] = useState({});
  const [unknownActions, setUnknownActions] = useState({});
  const [typeOverrides, setTypeOverrides] = useState({});

  // review
  const [plan, setPlan] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [allowPartial, setAllowPartial] = useState(false);
  const [jobId] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())));
  const [result, setResult] = useState(null);

  const entRows = useMemo(() => parsed?.entities?.rows || [], [parsed]);
  const relRows = useMemo(() => parsed?.relationships?.rows || [], [parsed]);

  // ---- step 1: parse ----
  const doParse = async (prefer) => {
    setBusy(true);
    try {
      const fd = new FormData();
      if (mode === "zip") {
        if (!zipFile) { toast.error("Choose a ZIP file"); setBusy(false); return; }
        fd.append("package", zipFile);
        if (prefer) fd.append("prefer_format", prefer);
      } else {
        if (!entityFile) { toast.error("Choose an entity file"); setBusy(false); return; }
        fd.append("entity_file", entityFile);
        if (relFile) fd.append("relationship_file", relFile);
      }
      const { data } = await api.post(`/imports/parse?workspace_id=${workspaceId}`, fd);
      if (data.needs_representation_choice) {
        setReprChoice(data.needs_representation_choice);
        setBusy(false);
        return;
      }
      if (!data.entities) { toast.error("No entity rows detected in the file."); setBusy(false); return; }
      setParsed(data);
      setReprChoice(null);
      setGroupName(data.group_name_default || "Imported contacts");
      setNamespace(data.source_namespace_default || "import");
      setEntityMapping(data.entities.suggested_mapping || {});
      setRelMapping(data.relationships?.suggested_mapping || {});
      // default unknown-column actions = ignore
      const ua = {};
      (data.entities.unknown_columns || []).forEach((c) => { ua[c] = "ignore"; });
      setUnknownActions(ua);
      setStep("configure");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not read the file.");
    } finally { setBusy(false); }
  };

  const basePayload = () => ({
    group: { name: groupName, description: groupDesc, folder_id: folderId || null },
    source_namespace: namespace,
    source_attribution: `File import (${parsed?.entities?.filename || "upload"})`,
    entity_mapping: entityMapping, rel_mapping: relMapping,
    unknown_actions: unknownActions, type_overrides: typeOverrides,
    entities: entRows, relationships: relRows, decisions,
  });

  // ---- step 2 -> review ----
  const doReview = async () => {
    if (!groupName.trim()) { toast.error("Group name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/review?workspace_id=${workspaceId}`, basePayload());
      setPlan(data);
      setStep("review");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Review failed");
    } finally { setBusy(false); }
  };

  // ---- step 3 -> commit ----
  const doCommit = async () => {
    setBusy(true);
    try {
      const baseVersions = {};
      (plan?.entities || []).forEach((e) => {
        if (e.canonical_id && e.base_version != null) baseVersions[e.canonical_id] = e.base_version;
      });
      const payload = { ...basePayload(), job_id: jobId, allow_partial: allowPartial,
        into_group_id: intoGroupId || null, base_versions: baseVersions };
      const { data } = await api.post(`/imports/commit?workspace_id=${workspaceId}`, payload);
      setResult(data);
      setStep("result");
      onComplete && onComplete();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 422 && detail?.plan) {
        setPlan(detail.plan);
        toast.error(detail.message || "Unresolved issues block this import.");
        setAllowPartial(true);
      } else {
        toast.error(apiError(detail) || "Import failed");
      }
    } finally { setBusy(false); }
  };

  const setDecision = (extId, val) => setDecisions((d) => ({ ...d, [extId]: val }));

  // completion downloads
  const downloadResults = () => {
    if (!result) return;
    const rows = [["kind", "row/id", "outcome", "detail"]];
    (result.created_entity_ids || []).forEach((id) => rows.push(["entity", id, "created", ""]));
    (result.failed_rows?.entities || []).forEach((f) => rows.push(["entity", `row ${f.row}`, "failed", (f.errors || []).join("; ")]));
    (result.created_rel_ids || []).forEach((id) => rows.push(["relationship", id, "created", ""]));
    (result.failed_rows?.relationships || []).forEach((f) => rows.push(["relationship", `row ${f.row}`, "failed", (f.errors || []).join("; ")]));
    downloadCsv("import_results.csv", rows);
  };

  const unknownTypes = useMemo(() => {
    const known = ["person", "organization", "organisation", "org", "company", "business", "place", "location", "event", "digital_space", "group"];
    const s = new Set();
    entRows.forEach((r) => {
      const col = Object.keys(entityMapping).find((k) => entityMapping[k] === "entity_type");
      const t = ((col ? r[col] : "") || "").trim().toLowerCase();
      if (t && !known.includes(t)) s.add(t);
    });
    return [...s];
  }, [entRows, entityMapping]);

  return (
    <div className="fixed inset-0 z-[1000] flex items-start justify-center p-4 overflow-y-auto" data-testid="import-wizard">
      <div className="absolute inset-0" style={{ background: "rgba(2,6,12,0.75)", backdropFilter: "blur(2px)" }} onClick={onClose} />
      <div className="relative my-6 w-full" style={{ maxWidth: 920, background: "var(--si-surface)", border: "1px solid var(--si-border)", borderRadius: 2 }}>
        <div className="flex items-center justify-between px-5 py-4 sticky top-0 z-10" style={{ borderBottom: "1px solid var(--si-border)", background: "var(--si-surface)" }}>
          <div className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--si-text)" }}>
            <Upload size={16} /> Import CSV / Excel
            <span className="si-label ml-2">{{ upload: "1 · Upload", configure: "2 · Name & map", review: "3 · Review", result: "4 · Done" }[step]}</span>
          </div>
          <button data-testid="import-close" onClick={onClose} style={{ color: "var(--si-text-2)" }}><X size={18} /></button>
        </div>

        <div className="p-5">
          {step === "upload" && (
            <div data-testid="import-step-upload">
              <div className="flex gap-2 mb-4">
                <Btn variant={mode === "files" ? "primary" : "secondary"} onClick={() => setMode("files")} data-testid="import-mode-files"><FileText size={14} /> CSV / Excel files</Btn>
                <Btn variant={mode === "zip" ? "primary" : "secondary"} onClick={() => setMode("zip")} data-testid="import-mode-zip"><FileSpreadsheet size={14} /> ZIP package</Btn>
              </div>

              {mode === "files" ? (
                <div className="space-y-4">
                  <UploadBox label="Entity file (required)" testid="import-entity-file" file={entityFile}
                    onFile={setEntityFile} accept=".csv,.xlsx" />
                  <UploadBox label="Relationship file (optional)" testid="import-rel-file" file={relFile}
                    onFile={setRelFile} accept=".csv,.xlsx" />
                </div>
              ) : (
                <UploadBox label="ZIP package (.zip containing CSV/XLSX)" testid="import-zip-file" file={zipFile}
                  onFile={setZipFile} accept=".zip" />
              )}

              {reprChoice && (
                <Panel className="p-3 mt-4" data-testid="import-repr-choice">
                  <div className="text-sm mb-2" style={{ color: "var(--si-warning)" }}>
                    This ZIP contains the same dataset in more than one format. Choose which representation to import (not both):
                  </div>
                  <div className="text-xs mb-2" style={{ color: "var(--si-text-2)" }}>
                    {reprChoice.datasets.map((d) => `${d.name}: ${d.formats.join(" / ")}`).join(" · ")}
                  </div>
                  <Select value={preferFormat} onChange={(e) => setPreferFormat(e.target.value)} style={{ width: "auto" }} data-testid="import-prefer-format">
                    <option value="csv">Use CSV</option>
                    <option value="xlsx">Use Excel (.xlsx)</option>
                  </Select>
                  <Btn variant="primary" className="ml-2" onClick={() => doParse(preferFormat)} disabled={busy} data-testid="import-repr-continue">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Continue
                  </Btn>
                </Panel>
              )}

              <div className="si-label mt-4" style={{ color: "var(--si-text-2)" }}>
                Limits: up to 5,000 rows and 10 MB per file. Values are read as text (leading zeros and phone prefixes preserved). Formulas and macros are never executed.
              </div>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <a className="text-xs underline" style={{ color: "var(--si-teal-light)" }} href={`${API}/imports/template?schema=entities&fmt=csv`} data-testid="tpl-entities-csv">Blank entity template (CSV)</a>
                <a className="text-xs underline" style={{ color: "var(--si-teal-light)" }} href={`${API}/imports/template?schema=entities&fmt=xlsx`} data-testid="tpl-entities-xlsx">Entity template (Excel)</a>
                <a className="text-xs underline" style={{ color: "var(--si-teal-light)" }} href={`${API}/imports/template?schema=relationships&fmt=csv`} data-testid="tpl-rel-csv">Relationship template (CSV)</a>
                <a className="text-xs underline" style={{ color: "var(--si-teal-light)" }} href={`${API}/imports/template?schema=relationships&fmt=xlsx`} data-testid="tpl-rel-xlsx">Relationship template (Excel)</a>
              </div>

              {!reprChoice && (
                <div className="flex justify-end gap-2 mt-5">
                  <Btn variant="secondary" onClick={onClose} data-testid="import-cancel-1">Cancel</Btn>
                  <Btn variant="primary" onClick={() => doParse()} disabled={busy} data-testid="import-parse-btn">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Read file
                  </Btn>
                </div>
              )}
            </div>
          )}

          {step === "configure" && parsed && (
            <div data-testid="import-step-configure" className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Group name" hint="One import creates one new group. Default is the entity filename.">
                  <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} data-testid="import-group-name" disabled={!!intoGroupId} />
                </Field>
                <Field label="Parent folder (optional)">
                  <Select value={folderId} onChange={(e) => setFolderId(e.target.value)} data-testid="import-folder" disabled={!!intoGroupId}>
                    <option value="">— unfiled —</option>
                    {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </Select>
                </Field>
              </div>
              {parsed.group_name_alternative && !intoGroupId && (
                <button className="text-xs underline" style={{ color: "var(--si-teal-light)" }}
                  onClick={() => setGroupName(parsed.group_name_alternative)} data-testid="import-use-group-col">
                  Use the "group" column value instead: “{parsed.group_name_alternative}”
                </button>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Description (optional)"><Input value={groupDesc} onChange={(e) => setGroupDesc(e.target.value)} data-testid="import-group-desc" disabled={!!intoGroupId} /></Field>
                <Field label="Source namespace" hint="Scopes external IDs (E0001…) so they never collide with other lists.">
                  <Input value={namespace} onChange={(e) => setNamespace(e.target.value)} data-testid="import-namespace" />
                </Field>
              </div>

              <Field label="Import into existing group instead (optional)" hint="Leave blank to create a new group (default).">
                <Select value={intoGroupId} onChange={(e) => setIntoGroupId(e.target.value)} data-testid="import-into-existing">
                  <option value="">— create new group —</option>
                  {existingGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </Select>
              </Field>

              {unknownTypes.length > 0 && (
                <Panel className="p-3" data-testid="import-type-overrides">
                  <div className="si-label mb-2" style={{ color: "var(--si-warning)" }}>Map unrecognised entity types</div>
                  {unknownTypes.map((t) => (
                    <div key={t} className="flex items-center gap-2 mb-1.5 text-sm">
                      <span className="font-mono" style={{ color: "var(--si-text)" }}>{t}</span> →
                      <Select value={typeOverrides[t] || ""} onChange={(e) => setTypeOverrides((o) => ({ ...o, [t]: e.target.value }))} style={{ width: "auto" }} data-testid={`type-override-${t}`}>
                        <option value="">choose…</option>
                        {["person", "organization", "place", "event", "group", "business"].map((o) => <option key={o} value={o}>{o}</option>)}
                      </Select>
                    </div>
                  ))}
                </Panel>
              )}

              <MappingTable title={`Entity columns (${entRows.length} rows)`} headers={parsed.entities.headers}
                mapping={entityMapping} setMapping={setEntityMapping} canonical={ENTITY_COLS}
                unknown={parsed.entities.unknown_columns || []} unknownActions={unknownActions} setUnknownActions={setUnknownActions} testid="entity" />

              {parsed.relationships && (
                <MappingTable title={`Relationship columns (${relRows.length} rows)`} headers={parsed.relationships.headers}
                  mapping={relMapping} setMapping={setRelMapping} canonical={REL_COLS}
                  unknown={[]} testid="rel" />
              )}

              {(parsed.warnings || []).concat(parsed.entities?.warnings || [], parsed.relationships?.warnings || []).map((w, i) => (
                <div key={i} className="text-xs flex items-start gap-1.5" style={{ color: "var(--si-warning)" }}><AlertTriangle size={12} className="mt-0.5" /> {w}</div>
              ))}

              <div className="flex justify-between gap-2 pt-2">
                <Btn variant="secondary" onClick={() => setStep("upload")} data-testid="import-back-1"><ArrowLeft size={14} /> Back</Btn>
                <div className="flex gap-2">
                  <Btn variant="secondary" onClick={onClose} data-testid="import-cancel-2">Cancel</Btn>
                  <Btn variant="primary" onClick={doReview} disabled={busy} data-testid="import-review-btn">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Review
                  </Btn>
                </div>
              </div>
            </div>
          )}

          {step === "review" && plan && (
            <div data-testid="import-step-review" className="space-y-4">
              <div className="flex flex-wrap gap-3 text-sm" data-testid="import-summary">
                <Stat label="New contacts" value={plan.summary.entities.new} />
                <Stat label="Linked/updated" value={plan.summary.entities.link} />
                <Stat label="Invalid" value={plan.summary.entities.invalid} danger={plan.summary.entities.invalid > 0} />
                <Stat label="Relationships OK" value={plan.summary.relationships.resolvable} />
                <Stat label="Rel. unresolved" value={plan.summary.relationships.unresolved} danger={plan.summary.relationships.unresolved > 0} />
              </div>
              <div className="si-label">Group: <span style={{ color: "var(--si-text)" }}>{intoGroupId ? (existingGroups.find((g) => g.id === intoGroupId)?.name) : plan.group.suggested}</span>{plan.group.exists && !intoGroupId && <span style={{ color: "var(--si-warning)" }}> (a group named “{plan.group.name}” exists — a unique name will be used)</span>}</div>

              <div className="overflow-x-auto" style={{ border: "1px solid var(--si-border)", borderRadius: 2, maxHeight: 260 }}>
                <table className="w-full text-xs">
                  <thead style={{ background: "var(--si-surface-hover)" }}><tr>
                    {["Row", "Ext ID", "Name", "Type", "Status", "Notes", "Action"].map((h) => <th key={h} className="text-left px-2 py-1.5 si-label font-normal">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {plan.entities.map((e) => (
                      <tr key={e.row} data-testid={`review-entity-${e.row}`} style={{ borderTop: "1px solid var(--si-border)" }}>
                        <td className="px-2 py-1.5 font-mono">{e.row}</td>
                        <td className="px-2 py-1.5 font-mono">{e.external_id || "—"}</td>
                        <td className="px-2 py-1.5" style={{ color: "var(--si-text)" }}>{e.name || <span style={{ color: "var(--si-danger)" }}>(missing)</span>}</td>
                        <td className="px-2 py-1.5">{e.type || "—"}</td>
                        <td className="px-2 py-1.5"><span style={{ color: STATUS_COLOR[e.status] || "var(--si-text-2)" }}>{e.status}</span></td>
                        <td className="px-2 py-1.5" style={{ color: "var(--si-text-2)", maxWidth: 240 }}>
                          {(e.errors || []).map((x, i) => <div key={i} style={{ color: "var(--si-danger)" }}>{x}</div>)}
                          {(e.warnings || []).map((x, i) => <div key={i} style={{ color: "var(--si-warning)" }}>{x}</div>)}
                          {(e.status === "link" || e.status === "already_imported") && e.current_values && (
                            <div style={{ color: "var(--si-silver)" }}>
                              Will update existing “{e.current_values.name}” — append note & add any new phone/email only (nothing erased).
                            </div>
                          )}
                          {(e.dup_candidates || []).map((d, i) => (
                            <div key={i}>Possible match: {d.name} ({d.evidence})
                              <button className="underline ml-1" style={{ color: "var(--si-teal-light)" }} onClick={() => setDecision(e.external_id, `link:${d.id}`)} data-testid={`review-link-${e.row}`}>Link</button>
                            </div>
                          ))}
                        </td>
                        <td className="px-2 py-1.5">
                          <Select value={decisions[e.external_id] || (e.status === "skipped" ? "skip" : "create")} onChange={(ev) => setDecision(e.external_id, ev.target.value)} style={{ width: "auto", padding: "2px 6px" }} data-testid={`review-decision-${e.row}`}>
                            <option value="create">Create</option>
                            <option value="skip">Skip</option>
                            {(e.dup_candidates || []).map((d) => <option key={d.id} value={`link:${d.id}`}>Link → {d.name}</option>)}
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {plan.relationships.length > 0 && (
                <div className="overflow-x-auto" style={{ border: "1px solid var(--si-border)", borderRadius: 2, maxHeight: 220 }}>
                  <table className="w-full text-xs">
                    <thead style={{ background: "var(--si-surface-hover)" }}><tr>
                      {["Row", "From", "Type", "To", "Direction", "Evidence", "Status"].map((h) => <th key={h} className="text-left px-2 py-1.5 si-label font-normal">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {plan.relationships.map((r) => (
                        <tr key={r.row} data-testid={`review-rel-${r.row}`} style={{ borderTop: "1px solid var(--si-border)" }}>
                          <td className="px-2 py-1.5 font-mono">{r.row}</td>
                          <td className="px-2 py-1.5" style={{ color: "var(--si-text)" }}>{r.from_name}</td>
                          <td className="px-2 py-1.5 font-mono">{r.type || "?"}</td>
                          <td className="px-2 py-1.5" style={{ color: "var(--si-text)" }}>{r.to_name}</td>
                          <td className="px-2 py-1.5">{r.direction}</td>
                          <td className="px-2 py-1.5">{r.rel_status}</td>
                          <td className="px-2 py-1.5">
                            <span style={{ color: STATUS_COLOR[r.status] || "var(--si-text-2)" }}>{r.status}</span>
                            {(r.errors || []).map((x, i) => <div key={i} style={{ color: "var(--si-danger)" }}>{x}</div>)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(plan.summary.entities.invalid > 0 || plan.summary.relationships.unresolved > 0) && (
                <label className="flex items-center gap-2 text-sm" style={{ color: "var(--si-warning)" }} data-testid="import-allow-partial-label">
                  <input type="checkbox" checked={allowPartial} onChange={(e) => setAllowPartial(e.target.checked)} data-testid="import-allow-partial" />
                  Import valid rows only and skip the unresolved ones
                </label>
              )}

              <div className="flex justify-between gap-2 pt-2">
                <Btn variant="secondary" onClick={() => setStep("configure")} data-testid="import-back-2"><ArrowLeft size={14} /> Back</Btn>
                <div className="flex gap-2">
                  <Btn variant="secondary" onClick={onClose} data-testid="import-cancel-3">Cancel</Btn>
                  <Btn variant="primary" onClick={doCommit} disabled={busy} data-testid="import-confirm-btn">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Confirm import
                  </Btn>
                </div>
              </div>
            </div>
          )}

          {step === "result" && result && (
            <div data-testid="import-step-result" className="space-y-4">
              <div className="flex items-center gap-2 text-base" style={{ color: "var(--si-teal-light)" }}><Check size={18} /> Import complete</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Contacts created" value={result.entities.created} />
                <Stat label="Linked / updated" value={result.entities.linked} />
                <Stat label="Skipped" value={result.entities.skipped} />
                <Stat label="Failed" value={result.entities.failed} danger={result.entities.failed > 0} />
                <Stat label="Group memberships" value={result.memberships} />
                <Stat label="Relationships created" value={result.relationships.created} />
                <Stat label="Relationships reused" value={result.relationships.reused} />
                <Stat label="Relationships failed" value={result.relationships.failed} danger={result.relationships.failed > 0} />
              </div>

              {(result.failed_rows?.entities?.length > 0 || result.failed_rows?.relationships?.length > 0) && (
                <Panel className="p-3" data-testid="import-failed-rows">
                  <div className="si-label mb-1" style={{ color: "var(--si-danger)" }}>Failed rows</div>
                  {(result.failed_rows.entities || []).map((f, i) => <div key={`e${i}`} className="text-xs" style={{ color: "var(--si-text-2)" }}>Entity row {f.row}: {(f.errors || []).join("; ")}</div>)}
                  {(result.failed_rows.relationships || []).map((f, i) => <div key={`r${i}`} className="text-xs" style={{ color: "var(--si-text-2)" }}>Relationship row {f.row}: {(f.errors || []).join("; ")}</div>)}
                </Panel>
              )}

              <div className="flex flex-wrap gap-2">
                <Btn variant="primary" onClick={() => { onClose(); onComplete && onComplete(result.group_id); }} data-testid="import-open-group"><Users size={14} /> Open group</Btn>
                <Btn variant="secondary" onClick={() => { const id = (result.created_entity_ids || [])[0]; if (id) window.open(`/app/entities/${id}`, "_self"); else toast.message("No new contact to open"); }} data-testid="import-view-web"><GitBranch size={14} /> View a contact</Btn>
                <Btn variant="ghost" onClick={downloadResults} data-testid="import-download-results"><Download size={14} /> Download results (CSV)</Btn>
                {(result.entities.failed > 0 || result.relationships.failed > 0) && (
                  <Btn variant="ghost" onClick={async () => {
                    setBusy(true);
                    try { const { data } = await api.post(`/imports/${jobId}/retry?workspace_id=${workspaceId}`); setResult(data); toast.success("Retried failed rows"); }
                    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Retry failed"); }
                    finally { setBusy(false); }
                  }} disabled={busy} data-testid="import-retry"><RotateCcw size={14} /> Retry failed</Btn>
                )}
                <Btn variant="ghost" onClick={onClose} data-testid="import-done">Done</Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadBox({ label, file, onFile, accept, testid }) {
  return (
    <div>
      <div className="si-label mb-1.5">{label}</div>
      <label className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors duration-200"
        style={{ border: "1px dashed var(--si-border)", borderRadius: 2, background: "var(--si-bg)" }} data-testid={testid}>
        <Upload size={16} style={{ color: "var(--si-teal-light)" }} />
        <span className="text-sm" style={{ color: file ? "var(--si-text)" : "var(--si-text-2)" }}>
          {file ? file.name : `Choose a file (${accept})`}
        </span>
        <input type="file" accept={accept} className="hidden" data-testid={`${testid}-input`}
          onChange={(e) => onFile(e.target.files?.[0] || null)} />
        {file && <button className="ml-auto" onClick={(e) => { e.preventDefault(); onFile(null); }} style={{ color: "var(--si-text-2)" }} data-testid={`${testid}-clear`}><X size={14} /></button>}
      </label>
    </div>
  );
}

function MappingTable({ title, headers, mapping, setMapping, canonical, unknown, unknownActions, setUnknownActions, testid }) {
  return (
    <div>
      <div className="si-label mb-2">{title}</div>
      <div className="overflow-x-auto" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }}>
        <table className="w-full text-xs">
          <thead style={{ background: "var(--si-surface-hover)" }}><tr>
            <th className="text-left px-2 py-1.5 si-label font-normal">File column</th>
            <th className="text-left px-2 py-1.5 si-label font-normal">Maps to</th>
          </tr></thead>
          <tbody>
            {headers.map((h) => {
              const isUnknown = (unknown || []).includes(h);
              return (
                <tr key={h} style={{ borderTop: "1px solid var(--si-border)" }}>
                  <td className="px-2 py-1.5 font-mono" style={{ color: "var(--si-text)" }}>{h}</td>
                  <td className="px-2 py-1.5">
                    {isUnknown ? (
                      <Select value={unknownActions?.[h] || "ignore"} onChange={(e) => setUnknownActions((o) => ({ ...o, [h]: e.target.value }))} style={{ width: "auto", padding: "2px 6px" }} data-testid={`map-${testid}-${h}`}>
                        <option value="ignore">Ignore</option>
                        <option value="note">Append to imported note</option>
                        <option value="custom">Create custom field</option>
                      </Select>
                    ) : (
                      <Select value={mapping[h] || ""} onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))} style={{ width: "auto", padding: "2px 6px" }} data-testid={`map-${testid}-${h}`}>
                        <option value="">— ignore —</option>
                        {canonical.map((c) => <option key={c} value={c}>{c}</option>)}
                      </Select>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, danger }) {
  return (
    <div className="px-3 py-2" style={{ border: "1px solid var(--si-border)", borderRadius: 2, minWidth: 110 }}>
      <div className="text-lg font-mono" style={{ color: danger ? "var(--si-danger)" : "var(--si-text)" }}>{value}</div>
      <div className="si-label">{label}</div>
    </div>
  );
}
